import {trans} from '@frontend/helpers'
import type {AdLocation} from '@frontend/modules/public/ads/AdsModuleTypes'
import {isLocationSelected} from '@frontend/modules/public/ads/components/search/helpers/isLocationSelected'
import clsx from 'clsx'
import {useMemo} from 'react'
import {Button} from 'swayok-react-mdb-ui-kit/components/Button/Button'
import {useVirtuosoLibAsync} from 'swayok-react-mdb-ui-kit/helpers/vendor/useVirtuosoLibAsync'

interface Props {
    header: string
    options: AdLocation[]
    selectedLocation: AdLocation | null
    keywordsRegexp: RegExp | null
    className?: string
    onSelect: (location: AdLocation) => void
    // Глубина локаций в списке.
    depth: number
}

// Группа локаций (колонка).
export function LocationSelectorOptionsGroup(props: Props) {

    const {
        header,
        options,
        selectedLocation,
        keywordsRegexp,
        className,
        onSelect,
        depth,
    } = props

    const virtuoso = useVirtuosoLibAsync<
        AdLocation,
        Pick<Props, 'selectedLocation'>
    >()

    // Отфильтрованные опции, которые нужно отобразить.
    const filteredOptions = useMemo(
        () => filterOptions(options, depth, keywordsRegexp, selectedLocation),
        [options, depth, keywordsRegexp?.toString(), selectedLocation?.id]
    )

    return (
        <div
            className="locations-group-column flex-1 flex-container-column"
            style={{
                height: 50 + Math.max(34 * filteredOptions.length, 200),
            }}
        >
            <div className="fw-500 px-3 pb-1">
                {header}
            </div>
            {filteredOptions.length > 0 ? (
                <virtuoso.Virtuoso
                    data={filteredOptions}
                    className={clsx('flex-1 border-top', className)}
                    context={{
                        selectedLocation,
                    }}
                    itemContent={(index, location, context) => (
                        <div
                            className={clsx(
                                'px-2',
                                index === 0 ? 'pt-2' : null,
                                filteredOptions.length - 1 === index ? 'pb-2' : null
                            )}
                        >
                            <Button
                                key={'location-' + location.id}
                                color="none"
                                ripple="blue"
                                className={clsx(
                                    'location-button',
                                    isLocationSelected(location, context.selectedLocation) ? 'active' : null
                                )}
                                onClick={() => onSelect(location)}
                            >
                                {location.name}
                            </Button>
                        </div>
                    )}
                />
            ) : (
                <div
                    className={clsx(
                        'flex-1 overflow-y-auto py-2 px-2 border-top',
                        className
                    )}
                >
                    <div className="no-location-found text-muted">
                        {trans().common.ads_filter.locations_modal.no_locations_found}
                    </div>
                </div>
            )}
        </div>
    )
}

// Фильтрация опций.
function filterOptions(
    options: AdLocation[],
    depth: number,
    keywordsRegexp: RegExp | null,
    selectedLocation: AdLocation | null
): AdLocation[] {
    if (keywordsRegexp) {
        return options.filter(location => (
            location.id === 0
            || keywordsRegexp.test(location.name)
        ))
    }
    if (
        depth > 0
        && selectedLocation
        && selectedLocation.id > 0
    ) {
        if (selectedLocation.parentIds.length < depth) {
            // Выбрана родительская категория.
            return options.filter(
                location => location.parentIds.includes(selectedLocation.id)
            )
        } else if (selectedLocation.parentIds.length === depth) {
            // Выбрана категория такого же уровня вложенности.
            const parentIdsStr = selectedLocation.parentIds.join(',')
            return options.filter(
                location => location.parentIds.join(',') === parentIdsStr
            )
        }
    }
    return options
}
